import React, { useMemo } from 'react';
import { Typography, Table, Alert, Tag, Space, Divider } from 'antd';
import { InfoCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { statusTokens, variantTokens } from '../../../theme/theme';

// Color constants from theme
const COLORS = {
    SUCCESS: statusTokens.success,
    WARNING: statusTokens.warning,
    ERROR: statusTokens.error,
    INFO: statusTokens.info,
    PRIMARY: variantTokens.primary,
};

const { Title, Text, Paragraph } = Typography;

/**
 * Step component for reviewing and selecting orderable items for quote
 * 
 * @param {Object} props - Component properties
 * @param {Object} props.summary - Summary of the analysis results
 * @param {Array} props.uploadedData - Raw uploaded data
 * @param {boolean} props.parsingFile - Whether file is being parsed
 * @param {Array} props.selectedRowKeys - Array of selected row keys
 * @param {Function} props.onSelectionChange - Callback when selection changes
 */
const OrderReviewStep = ({
    summary,
    uploadedData = [],
    parsingFile = false,
    selectedRowKeys = [],
    onSelectionChange
}) => {
    // 🛠️ Funzione per normalizzare i valori result (identica a ReviewSummaryStep)
    const normalizeResult = (result) => {
        return result ? result.toString().trim() : '';
    };

    // 🛠️ Calcola i conteggi REALI dai dati uploadedData
    const counts = useMemo(() => {
        const available = uploadedData.filter(item => {
            const normalized = normalizeResult(item.result);
            return normalized === 'Available' || normalized === 'found';
        });

        const orderable = uploadedData.filter(item => {
            const normalized = normalizeResult(item.result);
            return normalized === 'Orderable' || normalized === 'orderable';
        });

        const inProgress = uploadedData.filter(item => {
            const normalized = normalizeResult(item.result);
            return normalized === 'In progress';
        });

        const error = uploadedData.filter(item => {
            const normalized = normalizeResult(item.result);
            return normalized === 'Error' || normalized === 'invalid';
        });

        return {
            available: available.length,
            orderable: orderable.length,
            inProgress: inProgress.length,
            error: error.length,
            total: uploadedData.length,
            data: {
                available,
                orderable,
                inProgress,
                error
            }
        };
    }, [uploadedData]);

    // Function to get row key for items
    const getRowKey = (record) => {
        return record.skuCode || record.eanCode || record.upcCode || `item-${record.index || 'unknown'}`;
    };

    // Prepare data using real counts instead of summary
    const orderableTableData = useMemo(() => {
        return counts.data.orderable.map((item, index) => ({
            ...item,
            key: getRowKey(item),
            index: index + 1
        }));
    }, [counts.data.orderable]);



    // Table columns for orderable items (with selection)
    const orderableColumns = [
        {
            title: '#',
            dataIndex: 'index',
            key: 'index',
            width: 50,
        },
        {
            title: 'SKU Code',
            dataIndex: 'skuCode',
            key: 'skuCode',
            render: (text) => text || '-',
        },
        {
            title: 'EAN Code',
            dataIndex: 'eanCode',
            key: 'eanCode',
            render: (text) => text || '-',
        },
        {
            title: 'UPC Code',
            dataIndex: 'upcCode',
            key: 'upcCode',
            render: (text) => text || '-',
        },
        {
            title: 'Status',
            dataIndex: 'result',
            key: 'result',
            render: (status) => (
                <Tag color="green" icon={<CheckCircleOutlined />}>
                    {status}
                </Tag>
            ),
        }
    ];



    // Row selection configuration for orderable items
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            if (onSelectionChange && typeof onSelectionChange === 'function') {
                onSelectionChange(selectedKeys);
            }
        },
        getCheckboxProps: (record) => ({
            disabled: false,
        }),
    };

    if (parsingFile) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Text>Processing file...</Text>
            </div>
        );
    }

    return (
        <div style={{ padding: '1rem 0' }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <Title level={4}>Review Analysis Results</Title>
                <Paragraph>
                    Select the items you want to request a quote for. Only orderable items can be selected.
                </Paragraph>
            </div>

            {/* Summary Cards */}
            <div style={{ marginBottom: '2rem' }}>
                <Space size="large" wrap>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8c8c8c' }}>
                            {counts.available}
                        </div>
                        <Text type="secondary">Available</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: COLORS.SUCCESS }}>
                            {counts.orderable}
                        </div>
                        <Text type="secondary">Orderable</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: COLORS.WARNING }}>
                            {counts.inProgress}
                        </div>
                        <Text type="secondary">In The Making</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: COLORS.ERROR }}>
                            {counts.error}
                        </div>
                        <Text type="secondary">Error</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: COLORS.INFO }}>
                            {counts.total}
                        </div>
                        <Text type="secondary">Total</Text>
                    </div>
                </Space>
            </div>

            <Divider />

            {/* Orderable Items Section */}
            {counts.orderable > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                    <Alert
                        message="Orderable Items"
                        description="These items are available for ordering. Select the ones you want to request a quote for."
                        type="success"
                        icon={<CheckCircleOutlined />}
                        showIcon
                        style={{ marginBottom: '1rem' }}
                    />

                    <Table
                        columns={orderableColumns}
                        dataSource={orderableTableData}
                        rowSelection={rowSelection}
                        pagination={false}
                        size="small"
                        scroll={{ x: 600 }}
                        style={{ marginBottom: '1rem' }}
                    />
                </div>
            )}

            {/* Selection Summary */}
            {selectedRowKeys.length > 0 && (
                <Alert
                    message={`${selectedRowKeys.length} item(s) selected for quote request`}
                    type="info"
                    icon={<InfoCircleOutlined />}
                    showIcon
                    style={{ marginTop: '1rem' }}
                />
            )}

            {counts.orderable === 0 && (
                <Alert
                    message="No Orderable Items"
                    description="No items are currently available for ordering. Please check the error and in-progress items above."
                    type="warning"
                    showIcon
                    style={{ marginTop: '1rem' }}
                />
            )}
        </div>
    );
};

export default OrderReviewStep; 